import { useZero } from "@rocicorp/zero/react";
import { PUBLIC_ROUTE_AUTH_KILL_SWITCH_FLAG } from "../../../shared/featureFlagNames";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { config } from "../../environment";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { useHasAccess } from "../../hooks/useHasAccess";
import { useQuery } from "../../hooks/useQuery";
import { Text } from "../Text";
import { Toggle } from "../Toggle";
import { Tooltip } from "../Tooltip";
import { PanelCard, PanelHeader, PanelShell } from "./SettingsPanel";
import {
  Card,
  DetailLabel,
  DetailList,
  DetailRow,
  DetailRowRight,
  SectionHeading,
} from "./shared";

// When the deployment forces the route-auth kill switch on, the toggle is
// shown off (auth disabled) and locked, with a tooltip explaining why.
function KillSwitchToggle({
  isOn,
  onToggle,
  locked,
  lockedTooltip,
}: {
  isOn: boolean;
  onToggle: () => void;
  locked: boolean;
  lockedTooltip: string;
}) {
  const toggle = (
    <Toggle
      isOn={locked ? false : isOn}
      onToggle={onToggle}
      disabled={locked}
    />
  );
  return locked ? <Tooltip content={lockedTooltip}>{toggle}</Tooltip> : toggle;
}

function RowLabel({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <DetailLabel>
      <Text variant="text-regular-md" color="var(--text-primary)">
        {title}
      </Text>
      <Text variant="text-small" color="var(--text-secondary)">
        {description}
      </Text>
    </DetailLabel>
  );
}

export function SecuritySettings() {
  const z = useZero();
  const isTenantAdmin = useHasAccess("allow_all");
  const showKillSwitch = useFeatureFlag(PUBLIC_ROUTE_AUTH_KILL_SWITCH_FLAG);
  const killSwitchLocked = config.routeAuthForceDisabled;
  const [tenant] = useQuery(queries.currentTenant());
  const isExplore = !!tenant?.isExploreSignup;
  const lockedTooltip = isExplore
    ? "Not available in Explore Edition"
    : "Set by your system administrator";

  if (!isTenantAdmin) {
    return (
      <PanelShell>
        <PanelCard>
          <PanelHeader>
            <Text variant="display-md">Security</Text>
            <div style={{ marginTop: 8 }}>
              <Text variant="text-regular" color="var(--text-secondary)">
                Only admins can manage security settings
              </Text>
            </div>
          </PanelHeader>
        </PanelCard>
      </PanelShell>
    );
  }

  return (
    <PanelShell>
      <PanelCard>
        <PanelHeader>
          <Text variant="display-md">Security</Text>
          <div style={{ marginTop: 8 }}>
            <Text variant="text-regular" color="var(--text-secondary)">
              Control public access to your tenant.
            </Text>
          </div>
        </PanelHeader>

        {(showKillSwitch || killSwitchLocked) && tenant && (
          <Card style={{ width: "100%", maxWidth: 648 }}>
            <SectionHeading>
              <Text variant="text-regular-md">Public access</Text>
              <Text variant="text-small" color="var(--text-secondary)">
                Allow these surfaces to serve requests without signing in.
              </Text>
              {killSwitchLocked && isExplore && (
                <Text
                  variant="text-small"
                  color="var(--text-secondary)"
                  style={{ marginTop: 4 }}
                >
                  Public access is available on a paid plan. Book a demo to get
                  started.
                </Text>
              )}
            </SectionHeading>
            <DetailList>
              <DetailRow>
                <RowLabel
                  title="Public webpages"
                  description="Allow webpages with public auth to serve requests without signing in"
                />
                <DetailRowRight>
                  <KillSwitchToggle
                    locked={killSwitchLocked}
                    lockedTooltip={lockedTooltip}
                    isOn={!tenant.publicWebpageAuthDisabled}
                    onToggle={() =>
                      z.mutate(
                        sharedMutators.setRouteAuthPolicy({
                          publicWebpageDisabled:
                            !tenant.publicWebpageAuthDisabled,
                          previousPublicWebpageDisabled:
                            tenant.publicWebpageAuthDisabled ?? false,
                        })
                      )
                    }
                  />
                </DetailRowRight>
              </DetailRow>
              <DetailRow>
                <RowLabel
                  title="External ID webpages"
                  description="Allow webpages with external ID auth to serve requests without signing in"
                />
                <DetailRowRight>
                  <KillSwitchToggle
                    locked={killSwitchLocked}
                    lockedTooltip={lockedTooltip}
                    isOn={!tenant.externalIdWebpageAuthDisabled}
                    onToggle={() =>
                      z.mutate(
                        sharedMutators.setRouteAuthPolicy({
                          externalIdWebpageDisabled:
                            !tenant.externalIdWebpageAuthDisabled,
                          previousExternalIdWebpageDisabled:
                            tenant.externalIdWebpageAuthDisabled ?? false,
                        })
                      )
                    }
                  />
                </DetailRowRight>
              </DetailRow>
              <DetailRow>
                <RowLabel
                  title="Public webhooks & APIs"
                  description="Allow non-webpage routes with public auth to serve requests without signing in"
                />
                <DetailRowRight>
                  <KillSwitchToggle
                    locked={killSwitchLocked}
                    lockedTooltip={lockedTooltip}
                    isOn={!tenant.publicNonWebpageAuthDisabled}
                    onToggle={() =>
                      z.mutate(
                        sharedMutators.setRouteAuthPolicy({
                          publicNonWebpageDisabled:
                            !tenant.publicNonWebpageAuthDisabled,
                          previousPublicNonWebpageDisabled:
                            tenant.publicNonWebpageAuthDisabled ?? false,
                        })
                      )
                    }
                  />
                </DetailRowRight>
              </DetailRow>
            </DetailList>
          </Card>
        )}
      </PanelCard>
    </PanelShell>
  );
}
